import React, { useRef } from 'react';
import { useTableColumnResize } from './useTableColumnResize';

// Table — the standardized data-table for the whole app (tasks, efforts, PCRs, work IDs,
// admin lists…). Mirrors react-bootstrap's <Table> flags so it's a drop-in, but the look
// is centralized here so every table stays consistent. Design language:
//   • Header: quiet Title Case semibold labels on a tinted bar, separated from the body by
//     a stronger rule (so columns read as labels, distinct from data).
//   • Body: light row dividers, subtle hover, optional zebra striping.
//   • tabular-nums everywhere so figures line up column-to-column (key for cost tables).
// Pass `framed` for a self-contained rounded card (border + soft shadow + clipped corners);
// otherwise the table is bare and the caller controls its container.
export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  striped?: boolean;
  bordered?: boolean;
  borderless?: boolean;
  hover?: boolean;
  responsive?: boolean;
  size?: 'sm';
  variant?: string;
  /** Wrap in a rounded, bordered card (implies a horizontal scroll region). */
  framed?: boolean;
  /**
   * Sticky header cells. Only works when the nearest scrolling ancestor is the box that actually
   * scrolls the table - use it inside `TableScroll` / `DataTable fill`, and do not combine it with
   * `framed` or `responsive`, whose own overflow-x wrapper would catch the sticky positioning.
   */
  stickyHeader?: boolean;
  /**
   * V2-027 / TBL-18: drag a header divider to resize its column (default on; tables of four
   * columns or fewer are skipped). Pass false for a table that must keep fixed widths.
   */
  resizable?: boolean;
}

/**
 * Tooltip text for a header cell, so an ellipsized label is still recoverable.
 *
 * Headers are deliberately single-line and ellipsized (see the class list below), which reads
 * well until a label is longer than its column — at which point the text is simply gone, with
 * no way to see it. That is the "headers on reports appear hidden for longer headers" report.
 * Returning the label as a `title` restores it on hover without changing the layout.
 *
 * Only plain text can be derived automatically; a header built from elements should pass its own
 * `title`. Hand-written `<th>`s outside `DataTable`/`SortableTh` should do the same.
 */
export function headerTitle(node: React.ReactNode): string | undefined {
  if (typeof node === 'string') return node || undefined;
  if (typeof node === 'number') return String(node);
  return undefined;
}

export const Table: React.FC<TableProps> = ({
  striped, bordered, borderless, hover, responsive, size, variant, framed, stickyHeader, resizable = true, className = '', children, ...rest
}) => {
  const tableRef = useRef<HTMLTableElement>(null);
  useTableColumnResize(tableRef, resizable);
  const cls = [
    // Marker classes for the pinned-column and sticky-header rules in tailwind.css (a pinned cell
    // repeats its row's zebra / hover colour).
    striped ? 'wrap-table-striped' : '',
    hover ? 'wrap-table-hover' : '',
    stickyHeader ? 'wrap-table-sticky' : '',
    'tw-w-full tw-border-collapse tw-text-left tw-align-middle tw-text-content tw-tabular-nums',
    // Header — standardized eyebrow style. White bg matches the card interior; a strong
    // bottom rule (below) is what separates it from the body. Labels are Title Case and
    // semibold, never UPPERCASE (W-M4): the text is shown exactly as written, so "OT Eligible"
    // and "Task ID" read as written. Headers stay on a single row and ellipsize if too long
    // (never wrap to two lines).
    '[&_thead_th]:tw-bg-surface [&_thead_th]:tw-align-middle [&_thead_th]:tw-text-xs [&_thead_th]:tw-font-semibold [&_thead_th]:tw-normal-case [&_thead_th]:tw-tracking-normal [&_thead_th]:tw-text-content-secondary',
    '[&_thead_th]:tw-whitespace-nowrap [&_thead_th]:tw-overflow-hidden [&_thead_th]:tw-text-ellipsis',
    borderless
      ? '[&_thead_th]:tw-border-b [&_thead_th]:tw-border-line-input'
      : bordered
      ? 'tw-border tw-border-line-input [&_th]:tw-border [&_td]:tw-border [&_th]:tw-border-line [&_td]:tw-border-line [&_thead_th]:tw-border-line-input'
      : stickyHeader
      // The sticky header draws its 2px rule as an inset shadow (a collapsed border would scroll
      // away with the body), so it does not also get the border.
      ? '[&_tbody_td]:tw-border-b [&_tbody_td]:tw-border-line'
      : '[&_thead_th]:tw-border-b-2 [&_thead_th]:tw-border-line-input [&_tbody_td]:tw-border-b [&_tbody_td]:tw-border-line',
    striped ? '[&_tbody_tr:nth-child(odd)]:tw-bg-surface-alt' : '',
    hover ? '[&_tbody_tr]:tw-transition-colors [&_tbody_tr:hover]:tw-bg-surface-hover' : '',
    size === 'sm'
      ? '[&_th]:tw-px-2.5 [&_th]:tw-py-1.5 [&_td]:tw-px-2.5 [&_td]:tw-py-1.5'
      : '[&_th]:tw-px-3 [&_th]:tw-py-2.5 [&_td]:tw-px-3 [&_td]:tw-py-2.5',
    className,
  ].filter(Boolean).join(' ');

  const table = <table ref={tableRef} className={cls} {...rest}>{children}</table>;

  if (framed) {
    return (
      <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-line-input tw-shadow-sm">
        <div className="tw-w-full tw-overflow-x-auto">{table}</div>
      </div>
    );
  }
  return responsive ? <div className="tw-w-full tw-overflow-x-auto">{table}</div> : table;
};

export default Table;
